import { Project } from "../../../types/interfaces";
import { ClimateIdentifiers } from "./ClimateIdentifier";

// Basic GeoJSON types
export type Coordinate = [number, number];
export type Polygon = {
  type: "Polygon";
  coordinates: Coordinate[][];
};

// Feature properties
export type FeatureProperties = {
  area: string;
  perimeter: string;
  bbox: BBox;
  center: string;
} | {
  admin_leve: number;
  fid: number;
  hasc: string | null;
  name: string;
  name_en: string | null;
  osm_admin_: string;
  population: number;
}

type Crs = {
  type: string;
  properties: {
    code: string;
  };
}

// GeoJSON Feature
export type Feature = {
  type: "Feature";
  properties: FeatureProperties;
  geometry: Polygon;
  crs?: Crs
};

// GeoJSON FeatureCollection
export type FeatureCollection = {
  type: "FeatureCollection";
  features: Feature[];
  name?: string;
  crs?: Crs;
};

// Message types
export type ReadyMessage = "ready";

export type UserDrawnAreaMessage = {
  title: "user-drawn-area";
  message: FeatureCollection;
};

// Add support for file uploaded messages
export type UserFileUploadedMessage = {
  title: "user-file-uploaded";
  message: FeatureCollection;
};

// Analysis result types
export type MeasureCategory = {
  index: string[];
  note: Record<string, string[]>;
  unit: Record<string, { label: string }>;
};

export type Dimensions = {
  measure: {
    category: MeasureCategory;
    label: string;
  };
};

export type AnalysisResultData = {
  values: number[];
  dimensions: Dimensions;
};

export type AnalysisResultMessage = {
  title: "analysis-result";
  message: AnalysisResultData;
};

export type InitialCameraMessage = {
  west: number;
  south: number;
  east: number;
  north: number;
}

export type BBox = [number, number, number, number];

// Function to calculate bounding box from polygon coordinates
export const calculateBBoxFromGeometry = (geometry: Polygon): BBox => {
  const coordinates = geometry.coordinates[0]; // First ring of the polygon

  let minLng = Infinity;
  let minLat = Infinity;
  let maxLng = -Infinity;
  let maxLat = -Infinity;

  coordinates.forEach(([lng, lat]) => {
    minLng = Math.min(minLng, lng);
    minLat = Math.min(minLat, lat);
    maxLng = Math.max(maxLng, lng);
    maxLat = Math.max(maxLat, lat);
  });

  return [minLng, minLat, maxLng, maxLat];
};

// Function to calculate bounding box from a FeatureCollection
export const calculateBBoxFromFeatureCollection = (featureCollection: FeatureCollection): BBox => {
  let minLng = Infinity;
  let minLat = Infinity;
  let maxLng = -Infinity;
  let maxLat = -Infinity;

  featureCollection.features.forEach(feature => {
    const coordinates = feature.geometry.coordinates[0]; // First ring of the polygon

    coordinates.forEach(([lng, lat]) => {
      minLng = Math.min(minLng, lng);
      minLat = Math.min(minLat, lat);
      maxLng = Math.max(maxLng, lng);
      maxLat = Math.max(maxLat, lat);
    });
  });

  return [minLng, minLat, maxLng, maxLat];
};

// Union type for all possible message types
export type MapMessage = ReadyMessage | UserDrawnAreaMessage | AnalysisResultMessage | UserFileUploadedMessage;

// Type guard functions to check message types
export function isReadyMessage(message: any): message is ReadyMessage {
  return message.includes("ready");
}

export function isUserDrawnAreaMessage(message: any): message is UserDrawnAreaMessage {
  return typeof message === "object" &&
    message.title === "user-drawn-area" &&
    message.message?.type === "FeatureCollection";
}

// Add a type guard for the analysis result message
export function isAnalysisResultMessage(message: any): message is AnalysisResultMessage {
  return typeof message === "object" &&
    message.title === "analysis-result" &&
    Array.isArray(message.message?.values) &&
    typeof message.message?.dimensions === "object";
}

export function isFeatureCollection(message: any): message is FeatureCollection {
  return typeof message === "object" &&
    message.type === "FeatureCollection" &&
    Array.isArray(message.features);
}

export function isFeature(message: any): message is Feature {
  return typeof message === "object" &&
    message.type === "Feature" &&
    Array.isArray(message.geometry.coordinates);
}

// Add type guard for user file uploaded message
export function isUserFileUploadedMessage(message: any): message is UserFileUploadedMessage {
  return typeof message === "object" &&
    message.title === "user-file-uploaded" &&
    message.message?.type === "FeatureCollection" &&
    Array.isArray(message.message?.features);
}

export const formatAnalysisResults = (result: AnalysisResultMessage): ClimateIdentifiers => {
  try {
    const values = result.message.values;
    const categories = result.message.dimensions.measure.category.index;

    const resultObject: ClimateIdentifiers = {
      mat: null,
      map: null,
      pet: null,
      elevation: null
    };

    categories.forEach((category: string, index: number) => {
      const value = +values[index].toFixed(3);
      const categoryLower = category.toLowerCase();
      if (categoryLower.startsWith('mean annual temperature'))
        resultObject.mat = value;
      else if (categoryLower.startsWith('mean annual precipitation'))
        resultObject.map = value;
      else if (categoryLower.startsWith('mean annual potential evapotranspiration'))
        resultObject.pet = value;
      else if (categoryLower.startsWith('mean of elevation data'))
        resultObject.elevation = value;
    });

    return resultObject;
  } catch (error) {
    console.error("Error formatting analysis results:", error);
    return {
      mat: null,
      map: null,
      pet: null,
      elevation: null
    };
  }
};

export function isValidJson(str: string): boolean {
  try {
    JSON.parse(str);
    return true;
  } catch (e) {
    return false;
  }
}

/**
 * Checks if a string is a valid URL
 */
export function isUrl(str: string): boolean {
  try {
    const url = new URL(str);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch (e) {
    return false;
  }
}

/**
 * Safely parses map_data field, which can be either:
 * - A JSON string containing FeatureCollection (for Exact tool)
 * - A URL string (for B-intact tool)
 * 
 * @param mapData - The map_data field from project
 * @returns An object with the parsed data and its type
 */
export function parseMapData(mapData: string | null | undefined): { type: 'feature-collection', data: FeatureCollection } |
{ type: 'url', data: string } |
{ type: 'none', data: null } {

  if (!mapData) {
    return { type: 'none', data: null };
  }

  // Check if it's a URL first (B-intact tool)
  if (isUrl(mapData)) {
    return { type: 'url', data: mapData };
  }

  // Try to parse as JSON (Exact tool)
  if (isValidJson(mapData)) {
    try {
      const parsed = JSON.parse(mapData);
      if (isFeatureCollection(parsed)) {
        return { type: 'feature-collection', data: parsed };
      }
    } catch (e) {
      console.error("Error parsing map_data as JSON:", e);
    }
  }

  // If neither URL nor valid FeatureCollection, return none
  return { type: 'none', data: null };
}

const getInitialCameraFromBBox = (bbox?: BBox): InitialCameraMessage => {
  if (!bbox) return {
    west: 0,
    south: 0,
    east: 0,
    north: 0
  };

  const [west, south, east, north] = bbox;
  return {
    west,
    south,
    east,
    north
  }
}

const BASE_MAP_CONFIG_EXACT = {
  source: "http://data.apps.fao.org/static/sites/ex-act/config.json",
  initSources: ["https://data.apps.fao.org/static/sites/ex-act/config.json"]
}

const BASE_CATALOG_ITEM = {
  description: "\n  <div class='geojson-template-root'>\n    <div class='geojson-template-curated-data' style='display: block;'>\n      <div class='tjs-feature_info_download_button__buttonToolbar'>\n        <button class=\"geojson-show-raw-data tjs-feature_info_download_button__geojsonTemplateActionButton\">\n          <i class=\"bi bi-file-earmark-code\"></i>&nbsp;\n          Show Raw Data\n        </button>\n        <button class=\"geojson-download tjs-feature_info_download_button__geojsonTemplateActionButton\">\n          <i class=\"bi bi-download\"></i>&nbsp;\n          Download this Table\n        </button>\n      </div>\n      <table style='width: 100%'>\n        <tr>\n    <td>Area</td>\n    <td>2901581.08 km<sup>2</sup></td>\n  </tr>\n        <tr>\n    <td>Perimeter</td>\n    <td>6785.81 km</td>\n  </tr>\n        <tr>\n    <td>Center</td>\n    <td>{Lat: 15.408\u00b0, Lon: 21.549\u00b0}</td>\n  </tr>\n        <tr>\n    <td>Bounding Box</td>\n    <td><?xml version=\"1.0\" encoding=\"UTF-8\"?>\n                <!-- Created with Inkscape (http://www.inkscape.org/) -->\n                <svg width=\"70mm\" height=\"53mm\" version=\"1.1\" viewBox=\"0 0 203.48 200.23\" xmlns=\"http://www.w3.org/2000/svg\">\n                 <g transform=\"translate(-214.76 -241.8)\">\n                  <g>\n                   <rect transform=\"translate(-2.5831e-6)\" x=\"261.35\" y=\"284.24\" width=\"112.51\" height=\"112.51\" fill=\"none\" stroke=\"#9d9d9d\" stroke-dasharray=\"3.00472, 3.00472\" stroke-width=\"1.0016\"/>\n                   <rect x=\"314.95\" y=\"280.72\" width=\"7.5006\" height=\"7.5005\" stroke=\"black\" fill=\"transparent\"/>\n                   <!-- South Square -->\n                   <rect x=\"314.95\" y=\"392.8\" width=\"7.5006\" height=\"7.5005\" stroke=\"black\" fill=\"transparent\"/>\n                  </g>\n                  <text x=\"318.85297\" y=\"421.80942\" alignment-baseline=\"middle\" font-size=\"16px\" text-anchor=\"middle\"><tspan x=\"318.85297\" y=\"421.80942\">South</tspan><tspan x=\"318.85297\" y=\"441.80942\">7.60\u00b0</tspan><tspan x=\"318.85297\" y=\"461.80942\"/><tspan x=\"318.85297\" y=\"481.80942\"/></text>\n                  <text x=\"328.52194\" y=\"413.10297\" alignment-baseline=\"middle\" font-size=\"16px\" text-anchor=\"middle\"><tspan x=\"328.52194\" y=\"413.10297\"/></text>\n                  <!-- East Square -->\n                  <rect x=\"370.12\" y=\"332.25\" width=\"7.5006\" height=\"7.5005\" stroke=\"black\" fill=\"transparent\"/>\n                  <text x=\"411\" y=\"331.71744\" alignment-baseline=\"middle\" font-size=\"16px\" text-anchor=\"middle\">\n                  <tspan x=\"411\" y=\"331.71744\">East</tspan>\n                  <tspan x=\"411\" y=\"351.71744\">29.35\u00b0</tspan></text>\n                  <text x=\"223\" y=\"331.71744\" alignment-baseline=\"middle\" font-size=\"16px\" text-anchor=\"middle\">\n                    <tspan x=\"223\" y=\"331.71744\">West</tspan><tspan x=\"223\" y=\"351.71744\">13.74\u00b0</tspan></text>\n                  <!-- West Square -->\n                  <rect x=\"257.6\" y=\"332.25\" width=\"7.5006\" height=\"7.5005\" stroke=\"black\" fill=\"transparent\"/>\n                  <!-- Big Dotted Rectangle -->\n                  <text x=\"318.59515\" y=\"253.96049\" alignment-baseline=\"middle\" font-size=\"16px\" text-anchor=\"middle\"><tspan x=\"318.59515\" y=\"253.96049\">North</tspan><tspan x=\"318.59515\" y=\"273.96051\">23.21\u00b0</tspan></text>\n                  <g fill-opacity=\".1\">\n                   <path class=\"clr-i-outline clr-i-outline-path-1\" d=\"m328.41 300.15-9.66-9.61-9.66 9.61a1 1 0 1 0 1.41 1.39l7.25-7.19v19.13a1 1 0 1 0 2 0v-19.13l7.25 7.19a1.0006 1.0006 0 0 0 1.41-1.42z\"/>\n                   <path class=\"clr-i-outline clr-i-outline-path-1\" d=\"m278.64 326.29-9.61 9.66 9.61 9.66a1 1 0 1 0 1.39-1.41l-7.19-7.25h19.13a1 1 0 1 0 0-2h-19.13l7.19-7.25a1.0006 1.0006 0 0 0-1.42-1.41z\"/>\n                   <path class=\"clr-i-outline clr-i-outline-path-1\" d=\"m356.7 345.71 9.61-9.66-9.61-9.66a1 1 0 1 0-1.39 1.41l7.19 7.25h-19.13a1 1 0 1 0 0 2h19.13l-7.19 7.25a1.0006 1.0006 0 0 0 1.42 1.41z\"/>\n                   <path class=\"clr-i-outline clr-i-outline-path-1\" d=\"m308.99 379.53 9.66 9.61 9.66-9.61a1 1 0 1 0-1.41-1.39l-7.25 7.19v-19.13a1 1 0 1 0-2 0v19.13l-7.25-7.19a1.0006 1.0006 0 0 0-1.41 1.42z\"/>\n                  </g>\n                 </g>\n                </svg>\n</td>\n  </tr>\n      </table>\n    </div>\n    <div class='geojson-template-raw-data' style='display:none;'>\n      <div class='tjs-feature_info_download_button__buttonToolbar'>\n        <button class=\"geojson-copy tjs-feature_info_download_button__geojsonTemplateActionButton\">\n          <i class=\"bi bi-copy\"></i>&nbsp;\n          Copy to Clipboard\n        </button>\n        <button class=\"geojson-download tjs-feature_info_download_button__geojsonTemplateActionButton\">\n          <i class=\"bi bi-download\"></i>&nbsp;\n          Download this Table\n        </button>\n        <button class=\"geojson-show-curated-data tjs-feature_info_download_button__geojsonTemplateActionButton\">\n          <i class=\"bi bi-table\"></i>&nbsp;\n          Show Curated Data\n        </button>\n      </div>\n      <pre style='height: 200px;overflow-y:auto;background: rgba(0, 0, 0, 0.1);margin:0;'>{\n  \"type\": \"FeatureCollection\",\n  \"features\": [\n    {\n      \"properties\": {\n        \"area\": \"2901581.08 km<sup>2</sup>\",\n        \"perimeter\": \"6785.81 km\",\n        \"bbox\": [\n          \"13.744\",\n          \"7.604\",\n          \"29.353\",\n          \"23.213\"\n        ],\n        \"center\": \"{lat: 15.408\u00b0, lng: 21.549\u00b0}\"\n      },\n      \"type\": \"Feature\",\n      \"geometry\": {\n        \"type\": \"Polygon\",\n        \"coordinates\": [\n          [\n            [\n              13.744358046889008,\n              23.21267187674519\n            ],\n            [\n              29.353057599737472,\n              23.21267187674519\n            ],\n            [\n              29.35305759973748,\n              7.603972323896726\n            ],\n            [\n              13.744358046889007,\n              7.603972323896726\n            ],\n            [\n              13.744358046889005,\n              23.21267187674519\n            ],\n            [\n              13.744358046889008,\n              23.21267187674519\n            ]\n          ]\n        ]\n      }\n    }\n  ]\n}</pre>\n    </div>\n  </div>",
  featureInfoTemplate: "\n<div class= 'geojson-template-root' >\n<div class= 'geojson-template-curated-data' style='display: block;' >\n<div class= 'tjs-feature_info_download_button__buttonToolbar' >\n<button class=\"geojson-show-raw-data tjs-feature_info_download_button__geojsonTemplateActionButton\">\n          <i class=\"bi bi-file-earmark-code\"></i>&nbsp;\n          Show Raw Data\n        </button>\n        <button class=\"geojson-download tjs-feature_info_download_button__geojsonTemplateActionButton\">\n          <i class=\"bi bi-download\"></i>&nbsp;\n          Download this Table\n        </button>\n      </div>\n      <table style='width: 100%'>\n        <tr>\n    <td>Area</td>\n    <td>2901581.08 km<sup>2</sup></td>\n  </tr>\n        <tr>\n    <td>Perimeter</td>\n    <td>6785.81 km</td>\n  </tr>\n        <tr>\n    <td>Center</td>\n    <td>{Lat: 15.408\u00b0, Lon: 21.549\u00b0}</td>\n  </tr>\n        <tr>\n    <td>Bounding Box</td>\n    <td><?xml version=\"1.0\" encoding=\"UTF-8\"?>\n                <!-- Created with Inkscape (http://www.inkscape.org/) -->\n                <svg width=\"70mm\" height=\"53mm\" version=\"1.1\" viewBox=\"0 0 203.48 200.23\" xmlns=\"http://www.w3.org/2000/svg\">\n                 <g transform=\"translate(-214.76 -241.8)\">\n                  <g>\n                   <rect transform=\"translate(-2.5831e-6)\" x=\"261.35\" y=\"284.24\" width=\"112.51\" height=\"112.51\" fill=\"none\" stroke=\"#9d9d9d\" stroke-dasharray=\"3.00472, 3.00472\" stroke-width=\"1.0016\"/>\n                   <rect x=\"314.95\" y=\"280.72\" width=\"7.5006\" height=\"7.5005\" stroke=\"black\" fill=\"transparent\"/>\n                   <!-- South Square -->\n                   <rect x=\"314.95\" y=\"392.8\" width=\"7.5006\" height=\"7.5005\" stroke=\"black\" fill=\"transparent\"/>\n                  </g>\n                  <text x=\"318.85297\" y=\"421.80942\" alignment-baseline=\"middle\" font-size=\"16px\" text-anchor=\"middle\"><tspan x=\"318.85297\" y=\"421.80942\">South</tspan><tspan x=\"318.85297\" y=\"441.80942\">7.60\u00b0</tspan><tspan x=\"318.85297\" y=\"461.80942\"/><tspan x=\"318.85297\" y=\"481.80942\"/></text>\n                  <text x=\"328.52194\" y=\"413.10297\" alignment-baseline=\"middle\" font-size=\"16px\" text-anchor=\"middle\"><tspan x=\"328.52194\" y=\"413.10297\"/></text>\n                  <!-- East Square -->\n                  <rect x=\"370.12\" y=\"332.25\" width=\"7.5006\" height=\"7.5005\" stroke=\"black\" fill=\"transparent\"/>\n                  <text x=\"411\" y=\"331.71744\" alignment-baseline=\"middle\" font-size=\"16px\" text-anchor=\"middle\">\n                  <tspan x=\"411\" y=\"331.71744\">East</tspan>\n                  <tspan x=\"411\" y=\"351.71744\">29.35\u00b0</tspan></text>\n                  <text x=\"223\" y=\"331.71744\" alignment-baseline=\"middle\" font-size=\"16px\" text-anchor=\"middle\">\n                    <tspan x=\"223\" y=\"331.71744\">West</tspan><tspan x=\"223\" y=\"351.71744\">13.74\u00b0</tspan></text>\n                  <!-- West Square -->\n                  <rect x=\"257.6\" y=\"332.25\" width=\"7.5006\" height=\"7.5005\" stroke=\"black\" fill=\"transparent\"/>\n                  <!-- Big Dotted Rectangle -->\n                  <text x=\"318.59515\" y=\"253.96049\" alignment-baseline=\"middle\" font-size=\"16px\" text-anchor=\"middle\"><tspan x=\"318.59515\" y=\"253.96049\">North</tspan><tspan x=\"318.59515\" y=\"273.96051\">23.21\u00b0</tspan></text>\n                  <g fill-opacity=\".1\">\n                   <path class=\"clr-i-outline clr-i-outline-path-1\" d=\"m328.41 300.15-9.66-9.61-9.66 9.61a1 1 0 1 0 1.41 1.39l7.25-7.19v19.13a1 1 0 1 0 2 0v-19.13l7.25 7.19a1.0006 1.0006 0 0 0 1.41-1.42z\"/>\n                   <path class=\"clr-i-outline clr-i-outline-path-1\" d=\"m278.64 326.29-9.61 9.66 9.61 9.66a1 1 0 1 0 1.39-1.41l-7.19-7.25h19.13a1 1 0 1 0 0-2h-19.13l7.19-7.25a1.0006 1.0006 0 0 0-1.42-1.41z\"/>\n                   <path class=\"clr-i-outline clr-i-outline-path-1\" d=\"m356.7 345.71 9.61-9.66-9.61-9.66a1 1 0 1 0-1.39 1.41l7.19 7.25h-19.13a1 1 0 1 0 0 2h19.13l-7.19 7.25a1.0006 1.0006 0 0 0 1.42 1.41z\"/>\n                   <path class=\"clr-i-outline clr-i-outline-path-1\" d=\"m308.99 379.53 9.66 9.61 9.66-9.61a1 1 0 1 0-1.41-1.39l-7.25 7.19v-19.13a1 1 0 1 0-2 0v19.13l-7.25-7.19a1.0006 1.0006 0 0 0-1.41 1.42z\"/>\n                  </g>\n                 </g>\n                </svg>\n</td>\n  </tr>\n      </table>\n    </div>\n    <div class='geojson-template-raw-data' style='display:none;'>\n      <div class='tjs-feature_info_download_button__buttonToolbar'>\n        <button class=\"geojson-copy tjs-feature_info_download_button__geojsonTemplateActionButton\">\n          <i class=\"bi bi-copy\"></i>&nbsp;\n          Copy to Clipboard\n        </button>\n        <button class=\"geojson-download tjs-feature_info_download_button__geojsonTemplateActionButton\">\n          <i class=\"bi bi-download\"></i>&nbsp;\n          Download this Table\n        </button>\n        <button class=\"geojson-show-curated-data tjs-feature_info_download_button__geojsonTemplateActionButton\">\n          <i class=\"bi bi-table\"></i>&nbsp;\n          Show Curated Data\n        </button>\n      </div>\n      <pre style='height: 200px;overflow-y:auto;background: rgba(0, 0, 0, 0.1);margin:0;'>{\n  \"type\": \"FeatureCollection\",\n  \"features\": [\n    {\n      \"properties\": {\n        \"area\": \"2901581.08 km<sup>2</sup>\",\n        \"perimeter\": \"6785.81 km\",\n        \"bbox\": [\n          \"13.744\",\n          \"7.604\",\n          \"29.353\",\n          \"23.213\"\n        ],\n        \"center\": \"{lat: 15.408\u00b0, lng: 21.549\u00b0}\"\n      },\n      \"type\": \"Feature\",\n      \"geometry\": {\n        \"type\": \"Polygon\",\n        \"coordinates\": [\n          [\n            [\n              13.744358046889008,\n              23.21267187674519\n            ],\n            [\n              29.353057599737472,\n              23.21267187674519\n            ],\n            [\n              29.35305759973748,\n              7.603972323896726\n            ],\n            [\n              13.744358046889007,\n              7.603972323896726\n            ],\n            [\n              13.744358046889005,\n              23.21267187674519\n            ],\n            [\n              13.744358046889008,\n              23.21267187674519\n            ]\n          ]\n        ]\n      }\n    }\n  ]\n}</pre>\n    </div>\n  </div>",
  style: {
    "fill-opacity": 0.65
  },

  isUserSupplied: true,
  isPromoted: false,
  isHidden: false,
  forceProxy: false,
  customProperties: {
    hideRawDataButton: true,
    areaAnalysisParameter: true,
    cannotFocus: false
  },
  info: [],
  tags: [],
  shortReportSections: [],
  isLoading: false,
  isWaitingForDisclaimer: false,
  hideSource: false,
  nowViewingIndex: 1,
  isEnabled: true,
  isShown: true,
  isLegendVisible: true,
  displayChoicesBeforeLegend: false,
  isRefreshing: false,
  isEnableable: true,
  isRefresheable: true,
  isMappable: true,
  showsInfo: true,
  maximumShownFeatureInfos: 100,
  zoomOnEnable: false,
  useOwnClock: false,
  isTimeDisabled: false,
  disablePreview: false,
  dateFormat: {},
  clampToGround: true,
  extrudeScale: 10,
  opacity: 0.65,
  noBanner: 1,
  featureInfoTemplateInteractivity: {
    eventHandlers: {
      ".geojson-download": {
        event: "click"
      },
      ".geojson-show-raw-data": {
        event: "click"
      },
      ".geojson-show-curated-data": {
        event: "click"
      },
      ".geojson-copy": {
        event: "click"
      }
    }
  },
  polygonForAreaTimeSeries: false,
  type: "geojson",
  parents: [
    "Root Group/User-Added Data"
  ]
}

export const getMapConfig = (shape: FeatureCollection | null, project?: Project, isBintact?: boolean) => {
  if (!shape) return !isBintact ? BASE_MAP_CONFIG_EXACT : {};

  const feature = shape.features[0];
  // Try to get bbox from properties, or calculate it from geometry
  const bbox = ('bbox' in feature.properties && feature.properties.bbox)
    ? feature.properties.bbox
    : calculateBBoxFromGeometry(feature.geometry);
  const initialCamera = getInitialCameraFromBBox(bbox);
  const data = feature;

  const id = project ? project.id : `Polygon-Area-${Math.random()}`
  const name = project ? project.name : `Project Area-${Math.random()}`

  return {
    ...(!isBintact ? BASE_MAP_CONFIG_EXACT : { source: "BINTACT" }),
    initSources: [
      {
        initialCamera,
        catalog: [
          {
            type: "group",
            name: "Project Area",
            isOpen: true,
            order: 10,
            items: [
              {
                //DYNAMIC
                id: id.toString(),
                name,
                nameInCatalog: name,
                data,

                //STATIC
                ...BASE_CATALOG_ITEM
              }
            ]
          }
        ]
      },
      ...(!isBintact ? BASE_MAP_CONFIG_EXACT.initSources : [])
    ]
  }

}